/**
 * <header-popup> custom element
 *
 * Handles auto-open with configurable delay and frequency rules:
 *  - every_time: show on every qualifying page load
 *  - once_per_session: sessionStorage gate
 *  - once_per_day: localStorage with 24h expiry
 *  - cooldown: localStorage with configurable day expiry
 *  - max_total: localStorage counter up to N impressions
 *  - once_ever: localStorage permanent flag
 */
class HeaderPopup extends HTMLElement {
  connectedCallback() {
    this.triggerMode = this.dataset.triggerMode || 'auto';
    this.delay = parseInt(this.dataset.delay, 10) || 3;
    this.frequency = this.dataset.frequency || 'once_per_session';
    this.cooldownDays = parseInt(this.dataset.cooldownDays, 10) || 7;
    this.maxImpressions = parseInt(this.dataset.maxImpressions, 10) || 3;
    this.sectionId = this.dataset.sectionId;
    this.storageKey = `header-popup-${this.sectionId}`;
    this.preview = this.dataset.preview === 'true';

    // ModalDialog moves itself to document.body on connect, so query by ID
    this.modal = document.getElementById(`HeaderPopupModal-${this.sectionId}`);
    if (!this.modal) return;

    // Theme editor preview mode — bypass all rules, just show the popup
    if (window.Shopify && Shopify.designMode && this.preview) {
      this._forceOpen();
      this._listenEditorEvents();
      return;
    }

    // Mark seen on any close (only in non-preview mode)
    const closeBtn = this.modal.querySelector('[id^="ModalClose-"]');
    if (closeBtn) closeBtn.addEventListener('click', () => this.recordImpression());
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) this.recordImpression();
    });
    this.modal.addEventListener('keyup', (e) => {
      if (e.code === 'Escape') this.recordImpression();
    });

    // Auto-open
    if (this.shouldShow()) {
      this.autoOpenTimer = setTimeout(() => {
        this._forceOpen();
        this.recordImpression();
      }, this.delay * 1000);
    }
  }

  disconnectedCallback() {
    if (this.autoOpenTimer) clearTimeout(this.autoOpenTimer);
    if (this._editorHandler) {
      document.removeEventListener('shopify:section:load', this._editorHandler);
      document.removeEventListener('shopify:section:select', this._editorHandler);
      document.removeEventListener('shopify:block:select', this._editorHandler);
    }
  }

  /** Force the modal open — tries show(), falls back to setting the attribute */
  _forceOpen() {
    if (!this.modal) return;
    try {
      if (typeof this.modal.show === 'function') {
        this.modal.show(this);
      } else {
        this.modal.setAttribute('open', '');
      }
    } catch {
      this.modal.setAttribute('open', '');
    }
  }

  /** In theme editor preview mode, re-open modal on section/block events */
  _listenEditorEvents() {
    this._editorHandler = (evt) => {
      // Re-open on any section or block interaction for this section
      const detail = evt.detail || {};
      if (detail.sectionId === this.sectionId) {
        setTimeout(() => this._forceOpen(), 100);
      }
    };
    document.addEventListener('shopify:section:load', this._editorHandler);
    document.addEventListener('shopify:section:select', this._editorHandler);
    document.addEventListener('shopify:block:select', this._editorHandler);
  }

  shouldShow() {
    try {
      switch (this.frequency) {
        case 'every_time':
          return true;

        case 'once_per_session':
          return !sessionStorage.getItem(this.storageKey);

        case 'once_per_day':
          return this._checkExpiry(1);

        case 'cooldown':
          return this._checkExpiry(this.cooldownDays);

        case 'max_total': {
          const count = parseInt(localStorage.getItem(this.storageKey + '-count') || '0', 10);
          return count < this.maxImpressions;
        }

        case 'once_ever':
          return !localStorage.getItem(this.storageKey);

        default:
          return true;
      }
    } catch {
      return true;
    }
  }

  recordImpression() {
    try {
      switch (this.frequency) {
        case 'once_per_session':
          sessionStorage.setItem(this.storageKey, '1');
          break;

        case 'once_per_day':
        case 'cooldown': {
          const days = this.frequency === 'once_per_day' ? 1 : this.cooldownDays;
          const expires = Date.now() + days * 86400000;
          localStorage.setItem(this.storageKey, String(expires));
          break;
        }

        case 'max_total': {
          const count = parseInt(localStorage.getItem(this.storageKey + '-count') || '0', 10);
          localStorage.setItem(this.storageKey + '-count', String(count + 1));
          break;
        }

        case 'once_ever':
          localStorage.setItem(this.storageKey, '1');
          break;
      }
    } catch {
      // Storage unavailable
    }
  }

  _checkExpiry(days) {
    const stored = localStorage.getItem(this.storageKey);
    if (!stored) return true;
    return Date.now() > parseInt(stored, 10);
  }
}

if (!customElements.get('header-popup')) {
  customElements.define('header-popup', HeaderPopup);
}
